import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import SeatingChart from './SeatingChart';
import CalendarComponent from './CalendarComponent';
import KnowBeforeYouGoSection from './KnowBeforeYouGoSection';

const TicketSection = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [showSeatingChart, setShowSeatingChart] = useState(false);
  const { toast } = useToast();
  const seatingChartRef = useRef(null);

  const handleDateSelection = (date) => {
    setSelectedDate(date);
    setShowSeatingChart(true);
    setSelectedSeats([]);
  };

  // Scroll to seating chart when it becomes visible
  useEffect(() => {
    if (showSeatingChart && seatingChartRef.current) {
      setTimeout(() => {
        seatingChartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [showSeatingChart, seatingChartRef]);

  const handleSeatSelection = (seats) => {
    setSelectedSeats(seats);
  };

  const calculateTotal = () => {
    return selectedSeats.reduce((total, seat) => total + seat.price, 0);
  };

  const handleBooking = () => {
    if (!selectedDate || selectedSeats.length === 0) {
      toast({
        title: "Please complete your selection",
        description: "Select a date and at least one seat to continue.",
        variant: "destructive",
      });
      return;
    }

    // Mock booking process
    toast({
      title: "Booking initiated!",
      description: `Your ${selectedSeats.length} seat(s) for ${selectedDate} are being processed. Total: $${calculateTotal()}`,
    });
  };

  const handleBackToDateSelection = () => {
    setShowSeatingChart(false);
    setSelectedSeats([]);
  };

  return (
    <section id="tickets" className="py-20 bg-black text-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-purple-400">GET</span> TICKETS
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Secure your seats for Orlando's most captivating magic experience. Choose your preferred date and seats for an unforgettable evening.
            </p>
          </div>

          {/* Experience Packages Details */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">What's Included</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {/* VIP Experience */}
              <div className="bg-gradient-to-b from-yellow-900/30 to-gray-900/30 p-6 rounded-lg border border-yellow-500/30">
                <div className="flex items-center mb-4">
                  <div className="w-4 h-4 bg-yellow-600 rounded mr-3"></div>
                  <h4 className="text-xl font-bold text-yellow-400">VIP Experience</h4>
                  <span className="ml-auto text-lg font-bold text-yellow-400">$45</span>
                </div>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li className="flex items-center">
                    <span className="text-yellow-400 mr-2">✓</span>
                    Front row premium seating (Rows 1-2)
                  </li>
                  <li className="flex items-center">
                    <span className="text-yellow-400 mr-2">✓</span>
                    Exclusive Simon Nomis t-shirt
                  </li>
                  <li className="flex items-center">
                    <span className="text-yellow-400 mr-2">✓</span>
                    VIP pre-show meet & greet
                  </li>
                  <li className="flex items-center">
                    <span className="text-yellow-400 mr-2">✓</span>
                    Personal photo with Simon
                  </li>
                  <li className="flex items-center">
                    <span className="text-yellow-400 mr-2">✓</span>
                    Signed program and magic props
                  </li>
                </ul>
              </div>

              {/* Premium Experience */}
              <div className="bg-gradient-to-b from-blue-900/30 to-gray-900/30 p-6 rounded-lg border border-blue-500/30">
                <div className="flex items-center mb-4">
                  <div className="w-4 h-4 bg-blue-600 rounded mr-3"></div>
                  <h4 className="text-xl font-bold text-blue-400">Premium Experience</h4>
                  <span className="ml-auto text-lg font-bold text-blue-400">$35</span>
                </div>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li className="flex items-center">
                    <span className="text-blue-400 mr-2">✓</span>
                    Premium seating (Rows 3-4)
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-400 mr-2">✓</span>
                    Limited edition show poster
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-400 mr-2">✓</span>
                    Priority pre-show seating
                  </li>
                  <li className="flex items-center">
                    <span className="text-blue-400 mr-2">✓</span>
                    Complimentary program
                  </li>
                </ul>
              </div>

              {/* Standard Experience */}
              <div className="bg-gradient-to-b from-green-900/30 to-gray-900/30 p-6 rounded-lg border border-green-500/30">
                <div className="flex items-center mb-4">
                  <div className="w-4 h-4 bg-green-600 rounded mr-3"></div>
                  <h4 className="text-xl font-bold text-green-400">Standard Experience</h4>
                  <span className="ml-auto text-lg font-bold text-green-400">$30</span>
                </div>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li className="flex items-center">
                    <span className="text-green-400 mr-2">✓</span>
                    Reserved seating (Rows 5-10)
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-2">✓</span>
                    Full magic show experience
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-2">✓</span>
                    Access to merchandise store
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-2">✓</span>
                    Complimentary program
                  </li>
                </ul>
              </div>
            </div>

            {/* Buy Now Button */}
            <div className="text-center mt-8">
              <a 
                href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" 
                target="_blank" 
                rel="noreferrer"
                className="inline-block font-bold text-xl text-white rounded-full px-8 py-3 transition-all duration-300 hover:scale-105 hover:opacity-90"
                style={{ backgroundColor: 'rgb(181, 36, 189)' }}
              >
                Buy Tickets Now
              </a>
              <div className="mt-5 flex justify-center gap-1">
                <img alt="Visa Logo" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-visa.png" />
                <img alt="Mastercard Logo" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-mc.png" />
                <img alt="Amex Logo" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-amex.png" />
                <img alt="Discover Logo" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-discover.png" />
              </div>
            </div>
          </div>

          {!showSeatingChart ? (
            <div>
              {/* Know Before You Go Section */}
              <KnowBeforeYouGoSection />
              
              {/* Calendar Date Selection */}
              <div className="mb-8">
                <h3 className="text-3xl font-bold mb-6 text-center">Select Your Show Date</h3>
                <p className="text-center text-gray-400 mb-8">All shows start at 7pm</p>
                <CalendarComponent onDateSelection={handleDateSelection} />
              </div>
            </div>
          ) : (
            /* Seating Chart */
            <div ref={seatingChartRef}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-3xl font-bold">Select Your Seats</h3>
                  <p className="text-gray-400">
                    {new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })} at 7:00 PM
                  </p>
                </div>
                <Button 
                  onClick={handleBackToDateSelection}
                  variant="outline"
                  className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-black"
                >
                  ← Back to Dates
                </Button>
              </div>

              <SeatingChart 
                onSeatSelection={handleSeatSelection}
                selectedSeats={selectedSeats}
              />

              {/* Booking Summary */}
              {selectedSeats.length > 0 && (
                <div className="mt-8 bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-2xl font-bold mb-4">Booking Summary</h3>
                  <div className="space-y-2 text-gray-300 mb-4">
                    <div>Date: {new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</div>
                    <div>Time: 7:00 PM</div>
                    <div>Selected Seats: {selectedSeats.length}</div>
                    <div className="space-y-1">
                      {selectedSeats.map((seat) => (
                        <div key={seat.id} className="flex justify-between">
                          <span>Row {seat.row}, Seat {seat.seat} ({seat.type})</span>
                          <span>${seat.price}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xl font-bold text-purple-400 pt-2 border-t border-gray-600">
                      Total: ${calculateTotal()}
                    </div>
                  </div>
                  <Button
                    onClick={handleBooking}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 text-lg rounded-full transition-all duration-300 transform hover:scale-105"
                  >
                    Complete Booking - ${calculateTotal()}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TicketSection;